import type { ApiAccessClient } from '@communities/content-management/domain';
import { WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { ErrorCategory } from '@core/domain';
import { TextInput, LoadingSpinner, Dialog, Select } from '@soteria/design-system-ve';
import { useState, useEffect, useCallback } from 'react';

import { ClientIdListEditor } from './ClientIdListEditor';
import * as styles from './ProviderApplicationDialog.css';

/** Only Classification and Moderation workflows support API access client configuration */
const WORKFLOW_TYPE_OPTIONS = [
  { value: WorkflowType.CLASSIFICATION, label: 'Classification' },
  { value: WorkflowType.MODERATION, label: 'Moderation' },
];

interface ProviderApplicationDialogProps {
  provider?: ApiAccessClient | null;
  communityId: string;
  isOpen?: boolean;
  onClose?: () => void;
}

// Create default provider for new providers
const createDefaultProvider = (): ApiAccessClient => ({
  providerUrl: '',
  clientIdList: [''],
});

/** Derives form state from the initial provider, centralising default logic */
function getInitialFormState(provider?: ApiAccessClient | null) {
  // Strip workflowType (UI-only annotation) so it never leaks into component state or mutation payloads
  const { workflowType, ...providerData } = provider || createDefaultProvider();

  return {
    workflowType: workflowType || WorkflowType.CLASSIFICATION,
    providerUrl: providerData.providerUrl,
    clientIds: providerData.clientIdList,
  };
}

/** Builds the workflow configuration payload for upsert — pure function, no component state */
function buildWorkflowPayload(
  targetWorkflow: ReturnType<typeof useCommunityWorkflows>['workflows'],
  updatedClients: ApiAccessClient[],
  fallbackType: WorkflowType
) {
  return {
    ...targetWorkflow,
    type: targetWorkflow?.type || fallbackType,
    topicActions: targetWorkflow?.topicActions || [],
    apiSettings: {
      maxTps: targetWorkflow?.apiSettings?.maxTps || 100,
      monthlyBudget: targetWorkflow?.apiSettings?.monthlyBudget,
      accessClients: updatedClients,
    },
  };
}

/** Resolves the updated client list for the target workflow */
function resolveUpdatedClients(
  currentClients: ApiAccessClient[],
  updatedProvider: ApiAccessClient,
  existingProvider?: ApiAccessClient | null
): ApiAccessClient[] {
  if (existingProvider) {
    return currentClients.map(client => (client.providerUrl === existingProvider.providerUrl ? updatedProvider : client));
  }

  return [...currentClients, updatedProvider];
}

/** Returns a duplicate-check error message if client IDs contain duplicates, otherwise null */
function validateClientIds(clientIds: string[]): string | null {
  const hasDuplicates = clientIds.some((id, index) => clientIds.findIndex(otherId => otherId === id) !== index);

  return hasDuplicates ? 'Application IDs must be unique.' : null;
}

/** Derives dialog labels from the editing state — keeps ternaries out of the component body */
function getDialogLabels(isEditing: boolean) {
  const modeLabel = isEditing ? 'Edit' : 'Add';

  return {
    title: `${modeLabel} Provider Application`,
    description: `${modeLabel} OAuth2 authentication provider and client applications.\nClassification → /classify access | Moderation → /reports access`,
    submitLabel: isEditing ? 'Update Provider' : 'Save Provider',
  };
}

/**
 * Unified dialog for adding or editing authentication providers.
 * Includes a workflow type selector (Classification / Moderation) defaulting to Classification.
 * When editing, the workflow type is disabled — changing it requires delete + re-add.
 */
export function ProviderApplicationDialog({ provider: initialProvider, communityId, isOpen = false, onClose }: ProviderApplicationDialogProps) {
  // Load both workflow types — Apollo deduplicates to a single network request
  const { workflows: classificationWorkflow } = useCommunityWorkflows(communityId, WorkflowType.CLASSIFICATION);
  const { workflows: moderationWorkflow } = useCommunityWorkflows(communityId, WorkflowType.MODERATION);
  const { upsertWorkflow, loading: isSubmitting, errors, resetErrors } = useUpsertCommunityWorkflow();

  const isEditing = !!initialProvider;
  const { workflowType: defaultWorkflowType, providerUrl: defaultUrl, clientIds: defaultClientIds } = getInitialFormState(initialProvider);

  const [localWorkflowType, setLocalWorkflowType] = useState<WorkflowType>(defaultWorkflowType);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localProviderUrl, setLocalProviderUrl] = useState<string>(defaultUrl);
  const [localClientIds, setLocalClientIds] = useState<string[]>(defaultClientIds);

  const resetForm = useCallback(() => {
    const formState = getInitialFormState(initialProvider);

    setLocalWorkflowType(formState.workflowType);
    setLocalProviderUrl(formState.providerUrl);
    setLocalClientIds(formState.clientIds);
    setValidationError(null);
    resetErrors();
  }, [initialProvider, resetErrors]);

  // Reset form when dialog opens or provider changes
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const isFormValid = localProviderUrl.trim() !== '' && localClientIds.every(id => id.trim() !== '');

  // Handle form submission
  const handleSubmit = async () => {
    resetErrors();
    setValidationError(null);

    if (!isFormValid) {
      return;
    }

    const duplicateError = validateClientIds(localClientIds);

    if (duplicateError) {
      setValidationError(duplicateError);

      return;
    }

    // Construct payload explicitly — never spread provider state which may carry UI-only fields (e.g. workflowType)
    const updatedProvider: ApiAccessClient = { providerUrl: localProviderUrl, clientIdList: localClientIds };

    const targetWorkflow = localWorkflowType === WorkflowType.CLASSIFICATION ? classificationWorkflow : moderationWorkflow;
    const currentClients = targetWorkflow?.apiSettings?.accessClients || [];
    const updatedClients = resolveUpdatedClients(currentClients, updatedProvider, initialProvider);
    const updatedWorkflow = buildWorkflowPayload(targetWorkflow, updatedClients, localWorkflowType);
    const success = await upsertWorkflow(communityId, localWorkflowType, updatedWorkflow);

    if (success) {
      handleClose();
    }
  };

  const { title, description, submitLabel } = getDialogLabels(isEditing);
  const isSubmitDisabled = isSubmitting || !isFormValid;
  const hasErrors = !!errors && errors.length > 0;

  return (
    <Dialog
      customHeader={
        <>
          <h2 className={styles.dialogTitle}>{title}</h2>
          <p className={styles.dialogDescription}>{description}</p>
        </>
      }
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : submitLabel,
        onClick: handleSubmit,
        disabled: isSubmitDisabled,
      }}
      size="lg"
      onClose={handleClose}
    >
      <div className={styles.sectionContainer}>
        {/* Error banners wrapped in aria-live for screen reader announcement */}
        <div aria-live="assertive">
          {validationError && (
            <ErrorBanner
              errors={[{ code: 'VALIDATION', message: validationError, userMessage: validationError, category: ErrorCategory.VALIDATION }]}
              title="Validation Error:"
            />
          )}
          {hasErrors && <ErrorBanner errors={errors} title="There was an error saving the provider application:" />}
        </div>

        {/* Workflow Type Selector */}
        <div className={styles.workflowTypeSelect}>
          <Select
            required
            aria-required="true"
            disabled={isSubmitting || isEditing}
            helperText={isEditing ? 'Workflow type cannot be changed. To use a different type, delete this provider and add a new one.' : undefined}
            label="Workflow Type"
            options={WORKFLOW_TYPE_OPTIONS}
            value={localWorkflowType}
            onChange={value => setLocalWorkflowType(value as WorkflowType)}
          />
        </div>

        <TextInput
          aria-required="true"
          className={styles.providerInput}
          disabled={isSubmitting}
          label="Provider URL *"
          placeholder="https://provider.example.com"
          value={localProviderUrl}
          onChange={setLocalProviderUrl}
        />

        <ClientIdListEditor clientIds={localClientIds} disabled={isSubmitting} onChange={setLocalClientIds} />
      </div>
    </Dialog>
  );
}
